import { QueryKey, RefetchOptions, hashKey, useInfiniteQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { notice } from '@/components/pbo/notice'
import { PboAlert } from '@/components/pbo/pboDialog'

/** 뷰 한 페이지 — 1.0 ag-grid `paginationPageSize={10000}` 와 같은 값. 생성본 페이저 기본값도 10,000 이다. */
export const VIEW_PAGE_SIZE = 10000
/** 전량 조회 상한 — 넘으면 여기까지만 받고 조건을 좁히라고 안내한다(LOGISPRD-15267). */
export const MAX_FETCH_ROWS = 100_000
/** 나머지 구간 동시 요청 수 */
const CONCURRENCY = 3
/**
 * 보는 화면이 없어진 뒤(탭 이동·조건 변경) 캐시를 남겨 두는 시간 — 1분 안에 돌아오면 다시 받지 않는다.
 * 최대 10만 행이라 길게 두지 않는다. 지난 조건 캐시도 이 시간에 지워져 따로 지우지 않는다. 탭을 닫으면 MenuOnSessionWrap.remove 가 바로 지운다.
 */
const CACHE_TIME_MS = 60 * 1000

export const FETCH_ALL_MESSAGES = {
  fetching: '데이터를 받는 중입니다. 잠시 후 다시 시도해 주세요.',
  partial: '일부 데이터를 받지 못했습니다. 다시 조회해 주세요.',
  failed: '조회에 실패했습니다. 다시 조회해 주세요.',
  truncated: `조회 결과가 ${MAX_FETCH_ROWS.toLocaleString()}건을 넘어 ${MAX_FETCH_ROWS.toLocaleString()}건까지만 표시합니다. 조건을 좁혀 주세요.`,
  uploadPageOnly: '현재 페이지만 적용됩니다. 나머지는 다음 페이지에서 이어서 진행해 주세요.',
  changed: (from: number, to: number) =>
    `조회하는 동안 다른 작업으로 데이터가 바뀌었습니다. (조회 시작 ${from.toLocaleString()}건 → 받는 중 ${to.toLocaleString()}건)\n지금 목록은 일부 행이 빠지거나 두 번 들어갔을 수 있습니다.\n[확인]을 누르면 처음부터 다시 조회합니다.`,
}

export interface FetchedChunk<T> {
  items: T[]
  totalCount: number
}

interface Batch<T> extends FetchedChunk<T> {
  /** 묶음 안에 size 보다 짧은 구간이 있다 = 마지막 구간까지 왔다 */
  short: boolean
  /** 구간마다 BE 가 알려 준 전체 건수 — 첫 구간 값과 다르면 받는 사이 데이터가 바뀌었다(LOGISPRD-15264) */
  totalCounts: number[]
}

/** 데이터 변경을 이미 알린 받기의 첫 묶음 — 화면이 언마운트돼도(탭 이동) 남아, 돌아왔을 때 다시 알리지 않고 못 알린 것은 알린다. */
const changeAlerted = new WeakSet<object>()

/** BE page(0-based) 의 시작 번호와 이번 묶음에서 받을 구간 수 */
interface ChunkRange {
  start: number
  count: number
}

export interface UseFetchAllRowsOptions<T> {
  /** 화면 queryKeys.list(params) — 맨 앞 메뉴코드 규약(MenuOnSessionWrap 캐시 제거)을 따른다. 다른 useQuery 와 같은 키를 쓰지 않는다. */
  queryKey: QueryKey
  /** BE 한 구간 요청. chunkSize 가 없으면 size 는 undefined 로 한 번만 불린다. */
  fetchPage: (page: number, size: number | undefined) => Promise<FetchedChunk<T>>
  /** BE size 상한. 없으면 1번 호출로 전량을 받는다. */
  chunkSize?: number
  /** 기본키 — 구간 경계에서 겹친 행을 거른다(먼저 받은 행을 남긴다). */
  getKey: (row: T) => string | number
  enabled?: boolean
}

export interface UseFetchAllRowsResult<T> {
  /** 지금까지 받은 행(구간 순서, 중복 제거, 최대 MAX_FETCH_ROWS). 받는 동안 늘어난다. */
  rows: T[]
  /** BE 가 알려 준 전체 건수(첫 구간 응답). */
  totalCount: number
  /** 첫 구간을 받는 중 — 이때만 화면을 막는다. */
  isFirstLoading: boolean
  /** 첫 구간 뒤 나머지 구간을 받는 중(재조회 중 포함) */
  isFetchingRest: boolean
  /** 전체가 MAX_FETCH_ROWS 를 넘어 일부만 받았다 */
  isTruncated: boolean
  /** 나머지 구간 중 실패가 있어 빠진 행이 있다 */
  isPartial: boolean
  error: Error | null
  /**
   * 처음 구간부터 다시 받는다. `cancelRefetch: false` 면 진행 중 요청에 합류한다(기본은 취소 후 새로).
   * 결과의 data = 받은 묶음들 — 첫 묶음의 totalCount 가 전체 건수다(다시 받은 직후 판정용. 나머지 구간은 이어서 받는다). error = 실패 사유.
   */
  refetch: (options?: RefetchOptions) => Promise<{ isError: boolean; error: Error | null; data?: FetchedChunk<T>[] }>
  /** 저장·확정·삭제·엑셀 다운로드 첫 줄에서 부른다(서버가 조회 조건으로 만드는 엑셀에 걸지는 화면이 정한다). 받는 중·일부 실패·조회 실패(재조회 실패 포함)면 안내하고 false. */
  guardAction: () => boolean
}

/**
 * 1.0 방식 전량 조회 — 첫 구간으로 totalCount 를 알고, 나머지 구간을 CONCURRENCY 개씩 묶어 받는다.
 *
 * useInfiniteQuery 의 page 하나 = 구간 묶음 하나. 첫 page 는 첫 구간만(화면 블록 로딩), 이후는 effect 가 hasNextPage 동안
 * fetchNextPage 를 이어 부른다. 행을 외부 state 에 쌓지 않는다 — 조건이 바뀌면 키가 달라 이전 응답이 섞이지 않고,
 * 재조회는 react-query 가 page 를 처음부터 다시 받고, 탭 복귀는 캐시의 page 들로 복원된다(CACHE_TIME_MS 안).
 * useQueries 로 구간마다 쿼리를 두지 않는 이유: size 상한 1,000 인 API 면 10만 건에 100개가 한꺼번에 나간다.
 */
export const useFetchAllRows = <T>({ queryKey, fetchPage, chunkSize, getKey, enabled = true }: UseFetchAllRowsOptions<T>): UseFetchAllRowsResult<T> => {
  // 종료 판정은 구간 번호로 센다(행 수가 아니라) — 서버가 page 를 무시해 같은 행을 돌려줘도 정해진 구간 수에서 끝난다.
  const lastChunkIndex = (totalCount: number) => (chunkSize ? Math.ceil(Math.min(totalCount, MAX_FETCH_ROWS) / chunkSize) - 1 : 0)

  const query = useInfiniteQuery<Batch<T>, Error, Batch<T>[], QueryKey, ChunkRange>({
    queryKey,
    initialPageParam: { start: 0, count: 1 },
    queryFn: async ({ pageParam }) => {
      const chunks = await Promise.all(Array.from({ length: pageParam.count }, (_, i) => fetchPage(pageParam.start + i, chunkSize)))
      return {
        items: chunks.flatMap((c) => c.items),
        totalCount: chunks[0]?.totalCount ?? 0,
        short: !chunkSize || chunks.some((c) => c.items.length < chunkSize),
        totalCounts: chunks.map((c) => c.totalCount),
      }
    },
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.short) return undefined
      const next = lastPageParam.start + lastPageParam.count
      const last = lastChunkIndex(allPages[0].totalCount)
      return next > last ? undefined : { start: next, count: Math.min(CONCURRENCY, last - next + 1) }
    },
    select: (data) => data.pages,
    // 탭·이전 조건으로 돌아올 때 전량을 다시 받지 않는다(마운트 재조회 끔) — 다시 받는 것은 [조회] 의 refetch 뿐이다.
    staleTime: Infinity,
    gcTime: CACHE_TIME_MS,
    enabled,
    meta: { errorToast: true },
  })
  const { data: pages, hasNextPage, isFetching, isError, fetchNextPage, refetch } = query

  // 응답이 즉시 오면 isFetching 이 false→true→false 로 한 렌더에 접혀 deps 가 안 바뀐다 — 받은 page 수로 매 묶음마다 다시 돈다.
  const pageCount = pages?.length ?? 0
  useEffect(() => {
    // fetchNextPage 는 enabled 를 보지 않는다 — 조회가 꺼지면 새 묶음을 시작하지 않는다(#1012 리뷰 daehwanryu).
    // fetchNextPage 는 reject 하지 않는다(실패는 query.error) — catch 는 floating promise 린트용이다.
    if (enabled && hasNextPage && !isFetching && !isError) fetchNextPage().catch(() => undefined)
  }, [enabled, hasNextPage, isFetching, isError, fetchNextPage, pageCount])

  const getKeyRef = useRef(getKey)
  getKeyRef.current = getKey
  const rows = useMemo(() => {
    const seen = new Set<string | number>()
    return (pages ?? [])
      .flatMap((p) => p.items)
      .filter((row) => {
        const key = getKeyRef.current(row)
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      .slice(0, MAX_FETCH_ROWS)
  }, [pages])

  const totalCount = pages?.[0]?.totalCount ?? 0
  // 받는 중 = 요청이 떠 있거나, 묶음 사이(응답 직후 effect 가 다음 묶음을 부르기 전)라 다음 묶음이 남았다.
  const busy = isFetching || (!!hasNextPage && !isError)
  const isFetchingRest = !!pages && busy
  const isTruncated = totalCount > MAX_FETCH_ROWS
  // 부분 결과는 데이터로 판정한다 — 실패 뒤 재조회가 시작되면 fetchMeta 가 비어 isFetchNextPageError 가 false 로 돌아간다.
  const isPartial = isError && !!hasNextPage

  // 받는 사이 다른 작업으로 건수가 바뀐 구간의 totalCount — 구간 경계가 밀려 행이 빠지거나 겹쳤을 수 있다.
  // 받은 행 수로 판정하지 않는다: 중복 제거로 정상 목록이 짧은 화면(egr06·gr09)을 오판한다. 건수가 같고 정렬 키만 바뀐 경우는 못 잡는다.
  const changedTotal = useMemo(() => pages?.flatMap((p) => p.totalCounts).find((t) => t !== totalCount), [pages, totalCount])
  const changedMessage = changedTotal === undefined ? null : FETCH_ALL_MESSAGES.changed(totalCount, changedTotal)

  // 받기(첫 묶음)마다 한 번 알린다 — 재조회면 첫 묶음이 새로 생겨 다시 띄우고, 이미 알린 캐시로 돌아오면(탭·이전 조건) 띄우지 않는다.
  // [확인] = 처음부터 다시 받는다(진행 중 구간은 취소). ESC 로 닫으면 그대로 둔다 — guardAction 으로 막지 않는다: 행이 계속 느는 목록은
  // 다시 받아도 또 바뀌어 저장·엑셀이 계속 막힌다(사용자 결정 2026-10-16).
  // onOk 는 promise 를 돌려주지 않는다 — 돌려주면 모달이 다시 받기가 끝날 때까지 열려 있다(PboConfirm 과 같은 이유).
  // ponytail: 다시 받은 첫 구간이 직전과 똑같으면 structural sharing 이 같은 객체를 돌려줘 그 받기의 알럿은 빠진다.
  //   받기마다 번호를 묶음에 넣으면 잡히지만 같은 응답에도 rows 참조가 바뀌어 화면 effect 가 다시 돈다 — 그 대가가 더 커서 두지 않았다.
  const firstBatch = pages?.[0]
  useEffect(() => {
    if (!changedMessage || !firstBatch || changeAlerted.has(firstBatch)) return
    changeAlerted.add(firstBatch)
    PboAlert.show(changedMessage, {
      onOk: () => {
        refetch().catch(() => undefined)
      },
    })
  }, [changedMessage, firstBatch, refetch])

  // 상한 안내는 같은 조건의 받기를 성공으로 마친 순간 한 번 — 실패(전역 실패 알럿이 뜬다)·캐시 복귀·조건 전환·데이터 변경(재조회 알럿이 뜬다) 때는 띄우지 않는다.
  const keyHash = hashKey(queryKey)
  const prevRef = useRef({ keyHash, busy: false })
  useEffect(() => {
    const prev = prevRef.current
    if (prev.keyHash === keyHash && prev.busy && !busy && isTruncated && !isError && !changedMessage) notice.failure(FETCH_ALL_MESSAGES.truncated)
    prevRef.current = { keyHash, busy }
  }, [keyHash, busy, isTruncated, isError, changedMessage])

  // 첫 구간을 받는 중(pages 없음)에도 막는다 — 화면이 버튼을 안 막았을 때 0건으로 다운로드·저장되지 않게.
  const guardAction = useCallback(() => {
    if (busy) {
      notice.failure(FETCH_ALL_MESSAGES.fetching)
      return false
    }
    if (isPartial) {
      notice.failure(FETCH_ALL_MESSAGES.partial)
      return false
    }
    // 첫 구간 실패 — 받은 page 가 없어 정상 0건과 구분되지 않는다(#1012 리뷰 daehwanryu).
    // 다 받은 뒤 재조회 실패도 막는다 — pages 는 이전 스냅샷(저장 전 값·낡은 rowVersion)이라 그 위에서 저장·확정이 열리면 안 된다(#1017 리뷰 hyunhopark).
    if (isError) {
      notice.failure(FETCH_ALL_MESSAGES.failed)
      return false
    }
    return true
  }, [busy, isPartial, isError])

  return {
    rows,
    totalCount,
    isFirstLoading: query.isLoading,
    isFetchingRest,
    isTruncated,
    isPartial,
    error: query.error,
    refetch,
    guardAction,
  }
}

/**
 * BE 상한이 없어 한 번에 전량을 받는 화면(useFetchAllRows 를 안 쓰는 곳)의 저장·확정·삭제·엑셀 첫 줄 — 받는 중이면 안내하고 false.
 * 받는 동안 누르면 곧 덮일 이전 조회 행으로 요청이 나간다(LOGISPRD-15269).
 */
export const guardFetching = (isFetching: boolean): boolean => {
  if (!isFetching) return true
  notice.failure(FETCH_ALL_MESSAGES.fetching)
  return false
}

/**
 * 같은 조회의 다음 묶음이 뒤에 붙었으면 붙은 행만 돌려준다 — 받은 행은 묶음이 와도 같은 객체로 남으므로 앞 행 참조가 그대로인지로 판정한다.
 * 아니면 null(새 조회·재조회) — 화면은 편집 state 를 통째로 갈아 끼운다. 붙은 경우까지 갈아 끼우면 받는 동안 한 편집이 다음 묶음에 지워진다.
 */
export const appendedRows = <T>(prev: readonly T[], next: readonly T[]): T[] | null =>
  // 길이가 같아도 앞 행이 그대로면 [](붙은 것 없음) — 전부 겹친 키·0건 구간이 와도 편집을 지우지 않는다(#1017 리뷰 hyunhopark)
  prev.length > 0 && next.length >= prev.length && prev.every((r, i) => r === next[i]) ? next.slice(prev.length) : null

/**
 * fetchPage 안에서 부른다 — 그 구간 응답의 totalCount 로 보아 마지막 구간이 아닌데 size 보다 짧게(빈 구간 포함) 왔으면 던진다.
 * 훅은 짧은 구간을 끝으로 보고 받기를 멈춰, 일부만 받은 목록이 정상처럼 보이고 guardAction 도 통과한다(#1018·#1019 리뷰, gi38 선례).
 * 잡는 범위: 응답의 건수(totalCount)와 실제로 준 행 수가 어긋나는 응답(BE 가 구간을 잘못 준 경우) — 첫 구간이면 조회 실패, 뒤 구간이면 일부 미수신(isPartial).
 * 못 잡는 범위: 받는 사이 데이터가 줄고 BE 가 그 구간 totalCount 도 줄여 세어 주는 경우 — 행 수로 판정하면 중복 제거로 정상 목록이 짧은 화면(egr06·gr09)을 오판해 두지 않았다.
 * 문구는 전역 실패 알럿이 `response.data.message` 에서 읽는다.
 */
export const throwIfCutShort = (page: number, size: number, received: number, totalCount: number): void => {
  if (received >= size || (page + 1) * size >= Math.min(totalCount, MAX_FETCH_ROWS)) return
  // 첫 구간이면 받은 행이 없다 — guardAction 이 띄우는 '조회 실패' 와 같은 문구로 알린다
  const message = page === 0 ? FETCH_ALL_MESSAGES.failed : FETCH_ALL_MESSAGES.partial
  throw Object.assign(new Error(message), { response: { data: { message } } })
}

/** 업로드 직후 — 행 수가 한 페이지를 넘으면 현재 페이지만 적용된다고 알린다. 알렸으면 true. */
export const noticeIfUploadExceedsPage = (rowCount: number, pageSize = VIEW_PAGE_SIZE): boolean => {
  if (rowCount <= pageSize) return false
  notice.failure(FETCH_ALL_MESSAGES.uploadPageOnly)
  return true
}

/**
 * 같은 조건 재조회가 성공한 뒤 — [조회] 를 누를 때 표에 보이던 행(`shownAtSearch`)과 다른 칸만 입력분에 남긴다.
 * 받는 중에도 표를 고칠 수 있어서다. 표는 고친 행만이 아니라 페이지 행 전체를 넘기므로 행이 아니라 칸으로 가른다 —
 * [조회] 전 입력·안 고친 행의 옛 값·화면이 붙인 값(EDITTED 등)은 보이던 값과 같아 빠진다. 남은 값은 행 일부라 화면은 받은 행 위에 덮어 읽는다.
 * 보이던 행에 없는 키(재조회로 버려지는 업로드 행)는 지운다.
 */
export const keepEditedSince = <T extends object>(edits: Map<string, Partial<T>>, shownAtSearch: Map<string, T>): void => {
  edits.forEach((now, key) => {
    const shown = shownAtSearch.get(key)
    if (!shown) {
      edits.delete(key)
      return
    }
    const changed = Object.entries(now).filter(([field, value]) => value !== shown[field as keyof T])
    if (changed.length) edits.set(key, Object.fromEntries(changed) as Partial<T>)
    else edits.delete(key)
  })
}
